<template>
  <!-- Row text uses <div>, not <p>: the app embed forces every <p> to iOS body type -->
  <tbody class="charge-row" :class="{ 'is-refunding': showRefundForm }">
    <tr class="charge-row__main">
      <td class="charge-row__amount">
        <div class="charge-row__amount-line">
          <span :class="{ 'charge-row__amount--failed': !paid, 'charge-row__amount--muted': fullyRefunded || noCharge }">{{ formatAsCurrency(amount) }}</span>
          <!-- Coaches ask how every total breaks down, so the fee breakdown stays one click away -->
          <VDropdown v-if="showBreakdown" theme="info-tooltip" class="charge-row__info-wrap">
            <button type="button" class="charge-row__info" aria-label="How this total breaks down"><i class="fa-solid fa-info" aria-hidden="true"></i></button>
            <template v-slot:popper>
              <dl class="charge-breakdown">
                <div><dt>Amount paid</dt><dd>{{ formatAsCurrency(row.amount_paid) }}</dd></div>
                <div><dt>Card processing fee</dt><dd>−{{ formatAsCurrency(row.processing_fee) }}</dd></div>
                <div><dt>WrestlingIQ fee</dt><dd>−{{ formatAsCurrency(row.wiq_fee) }}</dd></div>
                <div v-if="row.tax_amount > 0"><dt>Taxes collected</dt><dd>{{ formatAsCurrency(row.tax_amount) }}</dd></div>
                <div v-if="alreadyRefundedAmount > 0"><dt>Refunded</dt><dd>−{{ formatAsCurrency(alreadyRefundedAmount) }}</dd></div>
                <div class="charge-breakdown__total"><dt>Net to the team</dt><dd>{{ formatAsCurrency(row.net_amount) }}</dd></div>
              </dl>
              <div class="charge-breakdown__payout">{{ payoutText }}</div>
            </template>
          </VDropdown>
        </div>
      </td>
      <td class="charge-row__status">
        <div class="charge-row__status-line">
          <span class="charge-badge" :class="`charge-badge--${status.tone}`"><i :class="status.icon" aria-hidden="true"></i> {{ status.label }}</span>
          <!-- Receipt icons sit beside what they document to save a line; the label is in the tooltip -->
          <a v-if="originalReceipt" :href="originalReceipt.url" target="_blank" class="charge-row__receipt" :title="originalReceipt.label" :aria-label="originalReceipt.label"><i class="fa-solid fa-receipt" aria-hidden="true"></i></a>
        </div>
        <div v-if="statusDetail" class="charge-row__detail" :class="`charge-row__detail--${status.tone}`">
          {{ statusDetail }}
          <a v-for="receipt in refundReceipts" :key="receipt.key" :href="receipt.url" target="_blank" class="charge-row__receipt" :title="receipt.label" :aria-label="receipt.label"><i class="fa-solid fa-receipt" aria-hidden="true"></i></a>
        </div>
        <div v-if="row.coupon" class="charge-row__coupon"><coupon-pill :code="row.coupon.code"></coupon-pill></div>
      </td>
      <td class="charge-row__for">
        <div class="charge-row__title">{{ row.chargeable.description }}</div>
        <div class="charge-row__meta">{{ row.type_label }}<template v-if="row.type_label && paymentMethodText"> · </template><span v-if="paymentMethodText" class="charge-row__method">{{ paymentMethodText }}</span></div>
        <!-- Stacked rows have no Wrestler(s) column, so name them here -->
        <div v-if="showWrestlers && row.wrestlers.length > 0" class="charge-row__for-wrestlers">
          <template v-for="(wrestler, index) in row.wrestlers"><template v-if="index > 0">, </template><a v-if="!forAppEmbed" :key="wrestler.id" :href="`/wrestlers/${wrestler.id}`" target="_blank">{{ wrestler.full_name }}</a><template v-else>{{ wrestler.full_name }}</template></template>
        </div>
      </td>
      <td v-if="showBilledTo" class="charge-row__billed-to">
        <a v-if="billableUrl && !forAppEmbed" :href="billableUrl" target="_blank">{{ displayNameOrEmail }}</a>
        <template v-else>{{ displayNameOrEmail }}</template>
      </td>
      <td v-if="showWrestlers" class="charge-row__wrestlers">
        <template v-if="row.wrestlers.length > 0">
          <div v-for="wrestler in row.wrestlers" :key="wrestler.id">
            <a v-if="!forAppEmbed" :href="`/wrestlers/${wrestler.id}`" target="_blank">{{ wrestler.full_name }}</a>
            <template v-else>{{ wrestler.full_name }}</template>
          </div>
        </template>
        <span v-else class="charge-row__none">—</span>
      </td>
      <td class="charge-row__date">
        <span>{{ dateText }}</span>
        <span v-if="timeText" class="charge-row__time">{{ timeText }}</span>
        <span class="charge-row__date-payer"> · {{ paid ? 'Paid by' : 'Billed to' }} <a v-if="billableUrl && !forAppEmbed" :href="billableUrl" target="_blank">{{ displayNameOrEmail }}</a><template v-else>{{ displayNameOrEmail }}</template></span>
      </td>
      <td class="charge-row__actions">
        <div class="charge-row__buttons">
          <a v-if="canViewSubscription" :href="subscriptionUrl" class="btn btn-default btn-compact">View subscription</a>
          <button v-if="refundPossible" type="button" class="btn btn-default btn-compact" @click="showRefundForm = true">Refund</button>
        </div>
      </td>
    </tr>
    <tr v-if="showRefundForm" class="charge-row__refund">
      <td :colspan="columnCount">
        <refund-form :possible-refund-amount="possibleRefundAmount" :charge="row"></refund-form>
      </td>
    </tr>
  </tbody>
</template>
<script>
import { formattingMixin } from './mixins/formatting_mix';
import RefundForm from './refund_form.vue';
import CouponPill from './coupon_pill.vue';

// One charge in a charges table: the payments dashboard, the online store
// page, and the wrestler Payments tab (web and app embed). Callers pass the
// table headers; showBilledTo / showWrestlers drop columns to match.
export default {
  name: 'charge-row',
  mixins: [formattingMixin],
  components: { RefundForm, CouponPill },
  props: {
    row: {
      type: Object,
      required: true,
    },
    showRefundActions: {
      type: Boolean,
      default: false,
    },
    showBilledTo: {
      type: Boolean,
      default: true,
    },
    showWrestlers: {
      type: Boolean,
      default: true,
    },
    // Receipts, refunds, subscription and profile pages are web pages the
    // native webview can't open (and refund-form reloads the page).
    forAppEmbed: {
      type: Boolean,
      default: false,
    },
    // { type, id } of a non-admin viewer. Parents also see what the other
    // parent paid for their kids, but that subscription's page isn't theirs.
    viewerBillable: {
      type: Object,
      default: null,
    },
    // A run of nightly retries on the same invoice, folded into this row
    failures: {
      type: Number,
      default: 1,
    },
    dateRange: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showRefundForm: false,
    };
  },
  computed: {
    billableUrl() {
      // Parents can't open another parent's page (the co-parent who paid)
      const { billable } = this.row.billing_profile;
      if (this.viewerBillable && billable.type === 'ParentProfile' && !(billable.type === this.viewerBillable.type && billable.id === this.viewerBillable.id)) {
        return null;
      }
      switch (billable.type) {
        case 'ParentProfile':
          return `/parents/${this.row.billing_profile.billable.id}`;
        case 'CoachProfile':
          return `/coaches/${this.row.billing_profile.billable.id}`;
        default:
          return null;
      }
    },
    displayNameOrEmail() {
      return this.row.billing_profile.billable.display_name || this.row.billing_profile.email;
    },
    paid() {
      return this.row.status === 'successful';
    },
    amount() {
      return this.paid ? this.row.amount_paid : this.row.attempted_amount;
    },
    noCharge() {
      return this.paid && this.row.amount_paid === 0;
    },
    paidWithCard() {
      return this.paid && this.row.payment_method === 'card';
    },
    possibleRefundAmount() {
      if (!this.paid) {
        return 0;
      }
      return this.row.amount_paid - this.alreadyRefundedAmount;
    },
    alreadyRefundedAmount() {
      return _.sumBy(this.row.refunds, 'amount');
    },
    fullyRefunded() {
      return this.row.amount_paid > 0 && this.possibleRefundAmount === 0;
    },
    partiallyRefunded() {
      return this.row.amount_paid > 0 && this.alreadyRefundedAmount > 0 && !this.fullyRefunded;
    },
    refundPossible() {
      return !this.forAppEmbed && this.paidWithCard && !this.fullyRefunded && this.showRefundActions && this.row.amount_paid !== 0;
    },
    showBreakdown() {
      return this.paid && this.showRefundActions && this.row.amount_paid > 0;
    },
    isRecurring() {
      return this.row.chargeable && this.row.chargeable.type === 'InvoicePayment';
    },
    canViewSubscription() {
      if (!this.isRecurring || this.forAppEmbed) {
        return false;
      }
      const { billable } = this.row.billing_profile;
      return !this.viewerBillable || (billable.type === this.viewerBillable.type && billable.id === this.viewerBillable.id);
    },
    subscriptionUrl() {
      return `/invoice_payments/${this.row.chargeable.id}`;
    },
    status() {
      if (!this.paid) {
        return { label: this.failures > 1 ? `Failed ×${this.failures}` : 'Failed', tone: 'danger', icon: 'fa-solid fa-xmark' };
      }
      if (this.noCharge) {
        return { label: 'Free', tone: 'neutral', icon: 'fa-solid fa-gift' };
      }
      if (this.fullyRefunded) {
        return { label: 'Refunded', tone: 'neutral', icon: 'fa-solid fa-rotate-left' };
      }
      if (this.partiallyRefunded) {
        return { label: 'Partly refunded', tone: 'warning', icon: 'fa-solid fa-rotate-left' };
      }
      return { label: 'Paid', tone: 'success', icon: 'fa-solid fa-check' };
    },
    statusDetail() {
      if (!this.paid) {
        return this.row.decline_reason;
      }
      const latest = _.maxBy(this.row.refunds, 'created_at');
      if (this.partiallyRefunded) {
        return `${this.formatAsCurrency(this.alreadyRefundedAmount)} refunded ${this.shortDate(latest.created_at)}`;
      }
      if (this.fullyRefunded && latest) {
        return `Refunded ${this.shortDate(latest.created_at)}`;
      }
      return null;
    },
    originalReceipt() {
      if (this.forAppEmbed || !this.row.receipt || !this.row.receipt.url) {
        return null;
      }
      return { url: this.row.receipt.url, label: 'Receipt' };
    },
    refundReceipts() {
      if (this.forAppEmbed) {
        return [];
      }
      return this.row.refunds
        .filter((refund) => refund.receipt && refund.receipt.url)
        .map((refund) => ({ key: refund.id, url: refund.receipt.url, label: `Refund receipt · ${this.formatAsCurrency(refund.amount)} · ${this.shortDate(refund.created_at)}` }));
    },
    paymentMethodText() {
      // Nothing was charged to a card or collected in cash
      if (this.noCharge) {
        return null;
      }
      if (this.row.payment_method === 'card') {
        return /^\d{4}$/.test(this.row.last_4 || '') ? `Card •${this.row.last_4}` : 'Card';
      }
      return this.row.payment_method ? _.capitalize(this.row.payment_method) : null;
    },
    dateText() {
      return this.dateRange || this.shortDate(this.row.created_at);
    },
    timeText() {
      return this.dateRange ? null : moment(this.row.created_at).format('h:mm A');
    },
    payoutText() {
      if (this.row.payout && this.row.payout.deposits_at) {
        return `Deposited ${this.shortDate(this.row.payout.deposits_at)}`;
      }
      return 'Not deposited yet';
    },
    columnCount() {
      return 5 + (this.showBilledTo ? 1 : 0) + (this.showWrestlers ? 1 : 0);
    },
  },
  mounted() {
    this.$on('cancel-refund', this.hideRefundForm);
  },
  destroyed() {
    this.$off('cancel-refund', this.hideRefundForm);
  },
  methods: {
    hideRefundForm(charge) {
      if (charge.id === this.row.id) {
        this.showRefundForm = false;
      }
    },
    shortDate(value) {
      const date = moment(value);
      return date.year() === moment().year() ? date.format('MMM D') : date.format('MMM D, YYYY');
    },
  },
};
</script>
